<template>
  <div 
    id="toolbar" 
    :style="{ 'height': cst.TOOLBAR_HEIGHT + 'px', 'top': (relativeHeight + cst.TIMELINE_HEIGHT) + 'px' }">
    <button 
      id="about-button" 
      class="icon-holder" 
      @click.prevent="showAbout">
      <svg 
        class="icon"
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 32 32">
        <rect 
          width="32" 
          height="32"/>
        <path d="M11.88,14.18l-2.63-.94-.11.13,1.48,2.57Z"/>
        <path d="M7.87,14.11a2.22,2.22,0,0,1-.51.06H7.29v6.41l2.39-3.32Z"/>
        <path d="M22.25,18s0-.07,0-.1L13.32,14.7l-1.9,2.64,2.86,5a2,2,0,0,1,.43,0,2.38,2.38,0,0,1,1.63.64l.46-.27a2.51,2.51,0,0,1-.06-.55,2.4,2.4,0,0,1,2.39-2.4,2.36,2.36,0,0,1,1.84.88l1.76-1.11A2.34,2.34,0,0,1,22.25,18Z"/>
        <path d="M12.32,24.65A2.38,2.38,0,0,1,13,23l-2.5-4.35L7.3,23.08h0v1.25a2.66,2.66,0,0,0,4.08,2.25l1.25-.79A2.35,2.35,0,0,1,12.32,24.65Z"/>
        <path d="M22.77,16.55l0,0L18.43,9.85,18.2,9.7a2.3,2.3,0,0,1-.45.05A2.2,2.2,0,0,1,17,9.62l-2.76,3.84Z"/>
        <path d="M7.36,9.39a2.4,2.4,0,0,1,2.39,2.39s0,.05,0,.07l3,1.09,3-4.2A2.34,2.34,0,0,1,15.44,8L11.37,5.41A2.66,2.66,0,0,0,7.29,7.66V9.39Z"/>
        <path d="M24,15.72a2.35,2.35,0,0,1,.6-.08,2.4,2.4,0,0,1,1.25.35,2.63,2.63,0,0,0-1.25-2.24l-3.22-2Z"/>
        <circle 
          cx="24.64" 
          cy="18.03" 
          r="1.28"/>
        <circle 
          cx="19.13" 
          cy="22.08" 
          r="1.28"/>
        <circle 
          cx="14.71" 
          cy="24.65" 
          r="1.28"/>
        <circle 
          cx="7.36" 
          cy="11.78" 
          r="1.28"/>
        <circle 
          cx="17.75" 
          cy="7.36" 
          r="1.28"/>
      </svg>
    </button>

    <div 
      id="event-info"
      ref="event-info"
      :class="{ 'hidden': !filteredEvents.length }"
      class="info-pane"
      @click="copy('event-info')">
      <span id="file-name"><strong>{{ folderName }}</strong></span>
      <span id="resolution">({{ viewPortWidth }}x{{ viewPortHeight }})</span>
      <span id="event-number"><strong>#{{ events.indexOf(activeEvent) }}</strong></span>
      <span id="image-name">({{ activeEvent ? activeEvent.image : '' }})</span>
      <span id="event-time">
        <strong>{{ activeEvent && activeEvent.time ? formatTime(activeEvent.time) : '00:00' }}</strong>
      </span>
    </div>

    <div 
      id="controls" 
      :class="{ 'hidden': !filteredEvents.length }">
      <button 
        id="previous" 
        class="icon-holder" 
        @click.prevent="previousEvent">
        <svg 
          class="icon" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 12 16">
          <polygon points="12 0.5 3 6.93 3 0 0 0 0 16 3 16 3 9.07 12 15.5 12 0.5"/>
        </svg>
      </button>
      <button 
        id="next" 
        class="icon-holder" 
        @click.prevent="nextEvent">
        <svg 
          class="icon" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 12 16">
          <polygon points="9 0 9 6.93 0 0.5 0 15.5 9 9.07 9 16 12 16 12 0 9 0"/>
        </svg>
      </button>
    </div>

    <div 
      id="normalize" 
      :class="{ 'hidden': !filteredEvents.length, 'checked': normalizeChecked }">
      <input 
        id="normalize-checkbox"
        v-model="normalizeChecked"
        type="checkbox">

      <span id="normalize-icon">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 12.16 9.78">
          <path d="M12.16,5.75a4.21,4.21,0,0,1-1,2.86A3.15,3.15,0,0,1,8.68,9.78,4,4,0,0,1,7.22,9.5a6.22,6.22,0,0,1-1.6-1.08,7.51,7.51,0,0,0-1.3-1,1.93,1.93,0,0,0-.85-.2,1.55,1.55,0,0,0-1.29.58,2.5,2.5,0,0,0-.45,1.58L0,9.41A4.08,4.08,0,0,1,1,6.59,3.17,3.17,0,0,1,3.47,5.48,3.74,3.74,0,0,1,5,5.83,7.06,7.06,0,0,1,6.74,7,3.48,3.48,0,0,0,8.37,8l.31,0A1.66,1.66,0,0,0,10,7.41a2.54,2.54,0,0,0,.51-1.65Z"/>
          <path d="M12.16.27a4.23,4.23,0,0,1-1,2.86A3.15,3.15,0,0,1,8.68,4.3,3.77,3.77,0,0,1,7.22,4a6.22,6.22,0,0,1-1.6-1.08,7.51,7.51,0,0,0-1.3-1,2.07,2.07,0,0,0-.85-.19,1.53,1.53,0,0,0-1.29.58,2.5,2.5,0,0,0-.45,1.58L0,3.93A4.06,4.06,0,0,1,1,1.11,3.17,3.17,0,0,1,3.47,0,3.89,3.89,0,0,1,5,.34a7.36,7.36,0,0,1,1.7,1.21,3.39,3.39,0,0,0,1.63,1l.31,0A1.64,1.64,0,0,0,10,1.92,2.49,2.49,0,0,0,10.52.28Z"/>
        </svg>
      </span>

      <input 
        id="normalize-width"
        v-model.lazy="normalizedWithInput"
        :disabled="!useNormalize"
        type="text">
      <span id="normalize-x">x</span>
      <input 
        id="normalize-height"
        v-model.lazy="normalizedHeightInput"
        :disabled="!useNormalize"
        type="text">
    </div>

    <div 
      id="session-info"
      ref="session-info"
      :class="{ 'hidden': !filteredEvents.length }"
      class="info-pane"
      @click="copy('session-info')">
      <strong id="total-events">{{ totalEvents }}</strong>
      <strong id="total-distance">{{ formatNumber(totalDistance, 0) }}</strong>
    </div>

    <a 
      id="feedback-button" 
      class="icon-holder" 
      href="mailto:info@ux-flo.com">
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 18 19" 
        class="icon">
        <path d="M14,0H4A4,4,0,0,0,0,4V18a1,1,0,0,0,.62.92A.84.84,0,0,0,1,19a1,1,0,0,0,.71-.29L4.41,16H14a4,4,0,0,0,4-4V4A4,4,0,0,0,14,0ZM4.17,5.93A4.69,4.69,0,0,0,4,7.44V9H2V7.45a3.83,3.83,0,0,1,.12-.93,5.28,5.28,0,0,1,.34-.94A6.2,6.2,0,0,1,3,4.71,4.32,4.32,0,0,1,3.6,4l1.12.7A5.69,5.69,0,0,0,4.17,5.93Zm3,0A5,5,0,0,0,7,7.44V9H5V7.45a3.83,3.83,0,0,1,.12-.93,5.28,5.28,0,0,1,.34-.94A6.2,6.2,0,0,1,6,4.71,4.32,4.32,0,0,1,6.58,4l1.12.7A6.25,6.25,0,0,0,7.14,5.93ZM13,8.55a3.83,3.83,0,0,1-.12.93,5.28,5.28,0,0,1-.34.94,6.2,6.2,0,0,1-.51.87,4.32,4.32,0,0,1-.63.71l-1.12-.7a6.22,6.22,0,0,0,.55-1.23A4.72,4.72,0,0,0,11,8.56V7h2Zm3,0a3.83,3.83,0,0,1-.12.93,6.2,6.2,0,0,1-.34.94,6.2,6.2,0,0,1-.51.87,4.32,4.32,0,0,1-.63.71l-1.12-.7a6.22,6.22,0,0,0,.55-1.23A4.69,4.69,0,0,0,14,8.56V7h2Z"/>
      </svg>
    </a>

    <button 
      id="profile-button" 
      class="icon-holder"
      @click.prevent="hideProfile = !hideProfile">
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 16 19.67" 
        class="icon">
        <path d="M8,0A28.32,28.32,0,0,1,0,2.52V8A12.49,12.49,0,0,0,8,19.67,12.49,12.49,0,0,0,16,8V2.52A28.32,28.32,0,0,1,8,0ZM9,9.24v3.28a1,1,0,0,1-2,0V9.24A2,2,0,0,1,6,7.52a2,2,0,0,1,4,0A2,2,0,0,1,9,9.24Z"/>
      </svg>
    </button>

    <About/>

    <Profile 
      :class="{ 'hidden': hideProfile }"
      @hide="hideProfile = true"/>

    <CookieMessage/>

    <textarea 
      ref="copy-value"
      class="copy-value"/>
  </div>
</template>

<script>
import About from './about/About.vue'
import Profile from './profile/Profile.vue'
import CookieMessage from './profile/CookieMessage.vue'
import { mapState, mapGetters, mapMutations } from 'vuex'
import * as cst from '../lib/constants'
import { formatTime, formatNumber } from '../lib/functions'

const switchEvent = component => keyUpEvent => {
  switch (keyUpEvent.key) {
    case 'ArrowRight':
      component.nextEvent()
      break
    case 'ArrowLeft':
      component.previousEvent()
      break
  }
}

export default {
  name: 'Toolbar',
  components: {
    About,
    Profile,
    CookieMessage,
  },
  data() {
    return {
      cst,
      hideProfile: true,
    }
  },
  computed: {
    ...mapState([
      'folderName',
      'viewPortWidth',
      'viewPortHeight',
      'useNormalize',
      'normalizedWidth',
      'normalizedHeight',
      'events',
    ]),
    ...mapGetters(['relativeHeight', 'filteredEvents', 'activeEventNr', 'activeEvent', 'totalEvents', 'totalDistance']),
    normalizeChecked: {
      get() {
        return this.useNormalize
      },
      set(checked) {
        this.setUseNormalize(checked)
      },
    },
    normalizedWithInput: {
      get() {
        return this.normalizedWidth
      },
      set(width) {
        this.setNormalizedWidth(width)
      },
    },
    normalizedHeightInput: {
      get() {
        return this.normalizedHeight
      },
      set(height) {
        this.setNormalizedHeight(height)
      },
    },
  },
  created() {
    window.addEventListener('keydown', switchEvent(this))
  },
  destroyed() {
    window.removeEventListener('keydown', switchEvent(this))
  },
  methods: {
    ...mapMutations(['setUseNormalize', 'setNormalizedWidth', 'setNormalizedHeight']),
    formatTime,
    formatNumber,
    showAbout() {
      this.$eventBus.$emit('show-about')
    },
    previousEvent() {
      if (this.events && this.events.length) {
        const firstEventNr = parseInt(this.events[0].key)

        if (this.activeEventNr > firstEventNr) {
          const prevEvent = this.events[this.activeEventNr - 1]

          const query = { ...this.$route.query }
          if (prevEvent.time < query.minTime) {
            query.minTime = prevEvent.time
          }

          this.$router.push({ name: 'event', params: { eventNr: prevEvent.key }, query })
        }
      }
    },
    nextEvent() {
      if (this.events && this.events.length) {
        const lastEventNr = parseInt(this.events[this.events.length - 1].key)

        if (this.activeEventNr < lastEventNr) {
          const nextEvent = this.events[this.activeEventNr + 1]

          const query = { ...this.$route.query }
          if (nextEvent.time > query.maxTime) {
            query.maxTime = nextEvent.time
          }

          this.$router.push({ name: 'event', params: { eventNr: nextEvent.key }, query })
        }
      }
    },
    copy(ref) {
      const el = this.$refs[ref]
      const isUseClass = ' in-use'
      el.className += isUseClass
      const copyEl = this.$refs['copy-value']
      copyEl.value = el.innerText.replace(/,/g, '')
      copyEl.focus()
      copyEl.select()
      document.execCommand('copy')
      copyEl.value = ''
      setTimeout(() => {
        el.className = el.className.substring(0, el.className.length - isUseClass.length)
      }, 800)
    },
  },
}
</script>

<style lang="scss">
#toolbar {
  position: absolute;
  width: 100%;
  left: 0;
  background: #e5e5e5;
  display: grid;
  grid-template-areas: 'about event-info controls . normalize . session-info feedback profile';
  grid-template-columns: 32px auto 74px auto 180px auto 177px 32px 32px;
  grid-template-rows: 32px;
  place-items: center start;

  #about-button {
    padding: 0;
    grid-area: about;

    &::after {
      content: 'INFO';
    }

    .icon {
      rect {
        opacity: 0;
      }

      path,
      circle {
        fill: #ff00a4;
      }
    }
  }

  #event-info {
    grid-area: event-info;
    display: grid;
    grid-template-areas: '. file-name . resolution . event-number . image-name . event-time .';
    grid-template-columns: 12px auto 12px auto 24px auto 12px auto 24px auto 8px;
    grid-template-rows: 32px;
    place-items: center start;

    &:hover {
      background: #fff;
    }

    #file-name {
      grid-area: file-name;
    }

    #resolution {
      grid-area: resolution;
    }

    #event-number {
      grid-area: event-number;
    }

    #image-name {
      grid-area: image-name;
    }

    #event-time {
      grid-area: event-time;
    }
  }

  #controls {
    grid-area: controls;
    display: grid;
    grid-template-areas: 'prev next';
    grid-template-columns: 32px 32px;
    grid-template-rows: 32px;
    place-items: center center;
  }

  #normalize {
    grid-area: normalize;
  }

  #session-info {
    grid-area: session-info;
  }

  #feedback-button {
    grid-area: feedback;
    padding: 6px 8px;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  #feedback-button {
    svg path {
      fill: #ff00a4;
    }

    &:hover svg path {
      fill: #222;
    }

    &::after {
      content: 'FEED\A BACK';
      top: -24px;
    }
  }

  #profile-button {
    grid-area: profile;
    padding: 9px 8px;

    svg {
      width: 100%;
      height: 100%;
    }

    &::after {
      content: 'PRO\A FILE';
      top: -23px;
    }
  }

  #previous,
  #next {
    padding: 8px 10px;
  }

  #previous {
    grid-area: prev;
  }

  #next {
    grid-area: next;
  }

  #previous::after,
  #next::after {
    content: 'STEP';
    top: -10px;
  }

  .removed {
    display: none;
  }

  #normalize {
    display: grid;
    grid-template-areas: '. checkbox . icon . width . x . height .';
    grid-template-columns: 9px 12px 6px 12px 6px 53px 4px 12px 4px 53px 9px;
    grid-template-rows: 32px;
    place-items: center center;
  }

  #normalize-checkbox {
    grid-area: checkbox;
    margin: 0;
    padding: 0;
  }

  #normalize-icon svg {
    width: 12px;
    height: 12px;
  }

  #normalize-icon {
    grid-area: icon;
  }

  #normalize-icon svg path {
    fill: #4a4e54;
  }

  #normalize-width {
    grid-area: width;
  }

  #normalize-x {
    grid-area: x;
  }

  #normalize-icon,
  #normalize-x {
    text-align: center;
    width: 100%;
  }

  #normalize-height {
    grid-area: height;
  }

  #normalize input[type='text'] {
    background: #fff;
    box-shadow: 0 0.8px 1px #333;
    border-radius: 3px;
    width: 51px;
    height: 21px;
    text-align: center;
    padding: 0;
    margin: 0 auto;
  }

  #normalize input[type='text'][disabled] {
    color: #ccc;
    background: none;
    box-shadow: none;
    border: 1px solid #ccc;
    cursor: not-allowed;
  }

  #session-info {
    display: grid;
    grid-template-areas: 'total-events total-distance .';
    grid-template-columns: 60px 90px 32px;
    grid-template-rows: 32px;
    place-items: center end;
    background-position: 0 60%;
  }

  #session-info:hover {
    background: url(../assets/info-pane-bg.jpg) 10px 6px no-repeat;
  }

  #session-info::after {
    top: -7px;
  }

  #total-events {
    grid-area: total-events;
  }

  #total-distance {
    grid-area: total-distance;
  }

  #total-events,
  #total-distance {
    position: relative;
  }

  #total-events::after,
  #total-distance::after {
    position: absolute;
    top: 4px;
    font-size: 75%;
    font-weight: normal;
  }

  #total-events::after {
    right: -22px;
    content: 'evs';
  }

  #total-distance:after {
    right: -14px;
    content: 'px';
  }
}
</style>
